import { Dialog, DialogSurface, DialogBody, DialogContent, Option, DialogTitle, Input, DialogActions, Button, Field, Subtitle1, Dropdown, Checkbox } from "@fluentui/react-components";
import ConfirmationDialog from '../../dialogs/ConfirmationDialog';
import { FC, useEffect, useState } from "react";

import { Note, NoteCreateDTO } from "../../../models/note";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DismissRegular } from "@fluentui/react-icons";

interface NewNoteProps {
    note?: Note;
    open: boolean;
    onClose: () => void;
    onSave?: (note: NoteCreateDTO) => void;
    initialData?: Partial<NoteCreateDTO>;
    actionType?: 'hide' | 'block' | 'prio' | 'archive' | null;
}

const NewNote: FC<NewNoteProps> = ({ note, open, onClose, onSave, initialData, actionType }) => {
    const baseCategories = [
        'Uppföljning på plats',
        'Uppföljning digital',
        'Uppföljning administrativ',
        'Begärda uppgifter',
        'Inkomna handlingar',
        'Inkomna synpunkter',
        'Uppgifter från media',
        'Uppsägning av ramavtal',
        'Annans tillfälliga avstängning',
        'Anmodan om rättelse',
        'Övrigt'
    ];
    
    // Add action-specific categories based on actionType
    const getCategories = () => {
        const categories = [...baseCategories];
        if (actionType === 'hide') {
            categories.push("Synlighet");
        } else if (actionType === 'block') {
            categories.push("Blockering massutskick");
        } else if (actionType === 'prio') {
            categories.push("Prio");
        }
        return categories;
    };
    
    const categories = getCategories();
    
    const [formData, setFormData] = useState<NoteCreateDTO>({
        noteHeader: '',
        noteSubject: '',
        noteBody: '',
        isNotePublic: false,
        noteDate: new Date().toISOString()
    });
    
    const [prioValue, setPrioValue] = useState<number>(1);
    const [confirmPublicOpen, setConfirmPublicOpen] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            // Initialize form with initialData, existing note data, or defaults
            setFormData({
                noteHeader: initialData?.noteHeader || note?.noteHeader || '',
                noteSubject: initialData?.noteSubject || note?.noteSubject || '',
                noteBody: initialData?.noteBody || note?.noteBody || '',
                isNotePublic: initialData?.isNotePublic ?? note?.isNotePublic ?? false,
                noteDate: initialData?.noteDate || note?.noteDate || new Date().toISOString()
            });
            // Initialize prio value if available
            setPrioValue(1);
        }
    }, [open, note, initialData]);

    const handleFieldChange = (field: keyof NoteCreateDTO, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const doSave = () => {
        if (onSave) {
            const noteData: any = { ...formData };
            if (formData.noteSubject === 'Prio') {
                noteData.prio = prioValue;
            }
            onSave(noteData);
        }
        onClose();
    };

    const handleSave = () => {
        if (formData.isNotePublic) {
            setConfirmPublicOpen(true);
        } else {
            doSave();
        }
    };

    const isFormValid = formData.noteHeader.trim() !== '' && 
                       formData.noteSubject.trim() !== '' && 
                       formData.noteBody.trim() !== '' && 
                       formData.noteDate.trim() !== '';

    return (
        <Dialog modalType="alert" open={open} onOpenChange={(_, data) => !data.open && onClose()}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle
                action={
                    <Button
                      shape="circular"
                      appearance="subtle"
                      icon={<DismissRegular />}
                      onClick={onClose}
                    />
                }
              >
                <Subtitle1>Ny loggboksanteckning</Subtitle1>
              </DialogTitle>

              <DialogContent className="flex flex-col gap-3">
                <Field label="Rubrik:" required>
                    <Input 
                        value={formData.noteHeader}
                        onChange={e => handleFieldChange('noteHeader', e.target.value)}
                    />
                </Field>

                <Field label="Loggdatum:" required>
                    <DatePicker
                        value={formData.noteDate ? new Date(formData.noteDate) : undefined}
                        formatDate={(date?: Date) => date ? date.toLocaleDateString('sv-SE') : ''}
                        onSelectDate={(date: Date | null | undefined) =>
                            handleFieldChange('noteDate', date ? date.toISOString() : '')
                        }
                        showWeekNumbers={true}
                        firstWeekOfYear={1}
                        showMonthPickerAsOverlay={true}
                        placeholder="åååå-mm-dd"
                    />
                </Field>

                <Field label="Typ av anteckning:" required>
                    <Dropdown
                        value={formData.noteSubject}
                        onOptionSelect={(_, data) => {
                            if (data.optionValue) {
                                handleFieldChange('noteSubject', data.optionValue);
                            }
                        }}
                    >
                        {categories.map((category, index) => (
                            <Option key={index} value={category} text={category}>
                                {category}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                {formData.noteSubject === 'Prio' && (
                    <Field label="Prioritet:" required>
                        <Input
                            type="number"
                            value={prioValue.toString()}
                            min={1}
                            max={6}
                            onChange={(e) => {
                                const value = parseInt(e.target.value);
                                if (value >= 1 && value <= 6) {
                                    setPrioValue(value);
                                }
                            }}
                        />
                    </Field>
                )}

                <Field>
                    <Checkbox
                        label="Publik"
                        checked={formData.isNotePublic}
                        onChange={(_, data) => handleFieldChange('isNotePublic', data.checked)}
                    />
                </Field>

                <Field label="Beskrivning" required>
                    <ReactQuill
                        theme="snow"
                        value={formData.noteBody}
                        onChange={(value) => handleFieldChange('noteBody', value)}
                    />
                </Field>
              </DialogContent>

              <DialogActions>
                  <Button appearance="secondary" onClick={onClose}>
                      Avbryt
                  </Button>
                  <Button
                      appearance="primary"
                      disabled={!isFormValid}
                      onClick={handleSave}
                  >
                      Publicera
                  </Button>
              </DialogActions>

              <ConfirmationDialog
                isOpen={confirmPublicOpen}
                onClose={() => setConfirmPublicOpen(false)}
                onConfirm={() => { setConfirmPublicOpen(false); doSave(); }}
                title="Bekräfta publikt inlägg"
                text="Är du säker på att du vill skapa ett publikt loggboksinlägg?"
              />
            </DialogBody>
          </DialogSurface>
        </Dialog>
    );
};

export default NewNote;
